import React from 'react';
import styled from 'styled-components';

import { InfoBox } from '@opus1/harmony';
import { STAFF_IMPORT_PAGE_COPY } from '../constants/staffImport';
import { formatImportJobTimestamp } from '../helpers/formatImportJobTimestamp';
import { StaffImportScanningPanel } from './StaffImportScanningPanel';
import { StaffImportValidationBanner } from './StaffImportValidationBanner';
import { staffImportTokens } from '../styles/staffImportTokens';
import type { AnalyzeResult, StaffImportSource } from '../types/staffImport';

export interface StaffImportStatusPanelProps {
    importSource: StaffImportSource | null;
    uploadedFileName: string | null;
    isImportJobProcessing: boolean;
    analyzeResult: AnalyzeResult | null;
    completedAt: string | null;
    isReadyForImport: boolean;
    showGridValidation?: boolean;
}

const Panel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
`;

const LastSuccessfulJob = styled.p`
    margin: 0;
    font-family: 'Montserrat', sans-serif;
    font-size: ${staffImportTokens.typography.bodyDefault.fontSize};
    font-weight: ${staffImportTokens.typography.bodyDefault.fontWeight};
    color: ${staffImportTokens.colors.textDefault};
`;

const resolveSuccessMessage = (
    importSource: StaffImportSource | null,
    uploadedFileName: string,
): string => {
    if (importSource === 'website') {
        return STAFF_IMPORT_PAGE_COPY.websiteImportSuccessMessage(
            uploadedFileName,
        );
    }
    return STAFF_IMPORT_PAGE_COPY.uploadedSuccessMessage(uploadedFileName);
};

export const StaffImportStatusPanel: React.FC<StaffImportStatusPanelProps> = ({
    importSource,
    uploadedFileName,
    isImportJobProcessing,
    analyzeResult,
    completedAt,
    isReadyForImport,
    showGridValidation = false,
}) => {
    const isImportLoadingInProgress = isImportJobProcessing && !analyzeResult;

    if (!uploadedFileName && !analyzeResult && !isImportJobProcessing) {
        return null;
    }

    const formattedCompletedAt = formatImportJobTimestamp(completedAt);
    const showWebsiteScanningPanel =
        isImportLoadingInProgress && importSource === 'website';
    const showCsvProcessingMessage =
        isImportLoadingInProgress && importSource !== 'website';

    return (
        <Panel>
            {showWebsiteScanningPanel ? (
                <StaffImportScanningPanel
                    title={STAFF_IMPORT_PAGE_COPY.scanningTitle}
                    message={STAFF_IMPORT_PAGE_COPY.scanningMessage}
                />
            ) : showCsvProcessingMessage ? (
                <InfoBox variant="info">
                    {STAFF_IMPORT_PAGE_COPY.pollingMessage}
                </InfoBox>
            ) : uploadedFileName && !isImportJobProcessing ? (
                <InfoBox variant="info">
                    {resolveSuccessMessage(importSource, uploadedFileName)}
                </InfoBox>
            ) : null}
            {formattedCompletedAt ? (
                <LastSuccessfulJob id="staff-import-last-successful-job">
                    {STAFF_IMPORT_PAGE_COPY.lastSuccessfulJobMessage(
                        formattedCompletedAt,
                    )}
                </LastSuccessfulJob>
            ) : null}
            {showGridValidation ? null : (
                <>
                    {analyzeResult &&
                    analyzeResult.validation.invalidRowCount > 0 ? (
                        <StaffImportValidationBanner
                            issues={analyzeResult.validation.issues}
                        />
                    ) : null}
                    {isReadyForImport ? (
                        <InfoBox variant="success">
                            {STAFF_IMPORT_PAGE_COPY.readyForImportMessage}
                        </InfoBox>
                    ) : null}
                </>
            )}
        </Panel>
    );
};

export default StaffImportStatusPanel;
